import { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';

import FBoxContainer from '../../../components/base/FBoxContainer';
import FHButton from '../../../components/base/FHButton';
import { FORTA_ERROR } from '../../../utils/colors';
import {
  useClientModifierMatrix,
  useClientModifierMatrixActions,
  useServiceCodeOptions,
} from '../../hooks';
import {
  MODIFIER_MATRIX_SERVICE_CODE_LABELS,
  MODIFIER_MATRIX_SERVICE_CODES,
} from '../../constants/modifierMatrix';
import ClientModifierMatrixRow, {
  ACTIONS_CELL_SX,
  DELIVERY_METHOD_CELL_SX,
  LOCATION_CELL_SX,
  MODIFIER_CELL_SX,
  MODIFIER_ROW_SCROLL_SX,
  MODIFIER_ROW_SX,
  PAYOR_CELL_SX,
  SERVICE_CODE_CELL_SX,
} from '../clientModifierMatrixRow';
import { ADD_ROW_BUTTON_SX, CENTER_MESSAGE_SX } from './styles';

import type { IClientModifierMatrixRow } from '../../../utils/types';
import type { ClientModifierMatrixSectionProps } from './types';

const ModifiersTableHeader = () => (
  <Box sx={MODIFIER_ROW_SX}>
    <Box sx={PAYOR_CELL_SX}>
      <Typography textAlign="left" fontWeight="bold">
        Payor*
      </Typography>
    </Box>
    <Box sx={SERVICE_CODE_CELL_SX}>
      <Typography textAlign="left" fontWeight="bold">
        Service Code*
      </Typography>
    </Box>
    <Box sx={LOCATION_CELL_SX}>
      <Typography textAlign="center" fontWeight="bold">
        Location*
      </Typography>
    </Box>
    <Box sx={DELIVERY_METHOD_CELL_SX}>
      <Typography textAlign="center" fontWeight="bold">
        Delivery Method*
      </Typography>
    </Box>
    <Box sx={MODIFIER_CELL_SX}>
      <Typography textAlign="center" fontWeight="bold">
        Modifier 1
      </Typography>
    </Box>
    <Box sx={MODIFIER_CELL_SX}>
      <Typography textAlign="center" fontWeight="bold">
        Modifier 2
      </Typography>
    </Box>
    <Box sx={MODIFIER_CELL_SX}>
      <Typography textAlign="center" fontWeight="bold">
        Modifier 3
      </Typography>
    </Box>
    <Box sx={MODIFIER_CELL_SX}>
      <Typography textAlign="center" fontWeight="bold">
        Modifier 4
      </Typography>
    </Box>
    <Box sx={ACTIONS_CELL_SX} />
  </Box>
);

const ClientModifierMatrixSection = ({
  clientId,
  payorOptions,
  dependencies = {
    useClientModifierMatrix,
    useClientModifierMatrixActions,
    useServiceCodeOptions,
  },
}: ClientModifierMatrixSectionProps) => {
  const { rows, loading, loadError } = dependencies.useClientModifierMatrix(clientId);
  const { refetchRows, createRow, updateRow, deleteRow } =
    dependencies.useClientModifierMatrixActions(clientId);
  const serviceCodeOptions = dependencies.useServiceCodeOptions(
    MODIFIER_MATRIX_SERVICE_CODES,
    MODIFIER_MATRIX_SERVICE_CODE_LABELS,
  );

  const [modifierRows, setModifierRows] = useState<IClientModifierMatrixRow[]>([]);
  const [isNewModifierRowAdding, setIsNewModifierRowAdding] = useState(false);
  // Id of the existing row being edited; null when nothing is being edited or a brand-new
  // (id-less) row is being added. Tracking by id — not list index — keeps the right row in
  // edit mode when another row is deleted and the list re-indexes.
  const [editableModifierRowId, setEditableModifierRowId] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const isManagingModifierRow = isNewModifierRowAdding || editableModifierRowId !== null;

  // Latest clientId, readable from inside an in-flight async handler whose closure captured an
  // older clientId — used to skip post-mutation work (esp. the refetch) after navigating away.
  const clientIdRef = useRef(clientId);
  useEffect(() => {
    clientIdRef.current = clientId;
  }, [clientId]);

  // Whether this component is still mounted. An abandoned handler (section unmounted mid-mutation)
  // must NOT fire its refetch — that would dispatch a fetch for the old client and hijack the
  // shared slice's request-owner, leaving the next client's screen stuck loading.
  const mountedRef = useRef(true);
  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    [],
  );

  // Monotonic generation token. Each mutation captures the current value; only the latest
  // owns the lock/post-mutation state. A clientId check alone isn't enough: A(pending) →
  // navigate B → back to A and the original handler's clientId matches again. The token bumps
  // on BOTH a new mutation AND every navigation (below), so a pending op is invalidated the
  // moment we leave its client — even if the user only opens a fresh draft (not a new mutation)
  // on returning.
  const mutationTokenRef = useRef(0);

  // This route component is reused across clients (only the :clientId param changes), so any
  // in-progress add/edit MUST be discarded when the client changes — otherwise a draft could
  // bleed into another client's view. Clearing the management flags lets the sync effect below
  // repopulate from the new client's fetched rows; bumping the token invalidates any
  // still-pending mutation from before.
  useEffect(() => {
    mutationTokenRef.current += 1;
    setModifierRows([]);
    setIsNewModifierRowAdding(false);
    setEditableModifierRowId(null);
    setIsSaving(false);
  }, [clientId]);

  // Keep the local list in sync with the fetched rows whenever no row is being added/edited, so
  // a re-fetch after a mutation surfaces here without clobbering an in-progress draft.
  useEffect(() => {
    if (!isManagingModifierRow) {
      setModifierRows(rows);
    }
  }, [rows, isManagingModifierRow]);

  const handleAddModifierRow = () => {
    setModifierRows((current) => [
      {
        id: null,
        payor_id: null,
        service_code_id: null,
        location: null,
        delivery_method: null,
        modifier_1: null,
        modifier_2: null,
        modifier_3: null,
        modifier_4: null,
      },
      ...current,
    ]);
    setEditableModifierRowId(null); // A brand-new row has no id yet
    setIsNewModifierRowAdding(true);
  };

  const handleEditModifierRow = (rowId: number) => {
    setEditableModifierRowId(rowId);
  };

  // Post-mutation work (refetch + editing-state reset) must be skipped if we've navigated away
  // or a newer mutation has since started — otherwise a stale handler refetches the old client
  // or stomps the newer operation's state.
  const opStillOwnsState = (opToken: number, opClientId: number | string | undefined) =>
    mountedRef.current &&
    clientIdRef.current === opClientId &&
    mutationTokenRef.current === opToken;

  const handleSaveModifierRow = async (updatedRow: IClientModifierMatrixRow) => {
    // Empty/whitespace-only modifiers must be sent as null; the backend validates non-empty
    // values against ^[A-Za-z0-9]{2}$ and rejects "" with a 422.
    const normalizedModifiers = {
      modifier_1: updatedRow.modifier_1?.trim() || null,
      modifier_2: updatedRow.modifier_2?.trim() || null,
      modifier_3: updatedRow.modifier_3?.trim() || null,
      modifier_4: updatedRow.modifier_4?.trim() || null,
    };
    const opToken = (mutationTokenRef.current += 1);
    setIsSaving(true);
    try {
      const payload = {
        payor_id: updatedRow.payor_id,
        service_code_id: updatedRow.service_code_id,
        location: updatedRow.location,
        delivery_method: updatedRow.delivery_method,
        ...normalizedModifiers,
      };
      if (updatedRow.id) {
        await updateRow(payload, updatedRow.id).unwrap();
      } else {
        await createRow(payload).unwrap();
      }
      if (!opStillOwnsState(opToken, clientId)) return;
      setEditableModifierRowId(null);
      setIsNewModifierRowAdding(false);
      await refetchRows();
    } catch {
      // The slice's rejected reducer already surfaces the server error toast; swallow the
      // rejection here so the finally block can reset the saving state.
    } finally {
      // Release the lock only if this is still the latest mutation (token unchanged) and the
      // component is mounted — a stale op (client changed, or a newer A→B→A mutation started)
      // must not clear a newer operation's saving state.
      if (mountedRef.current && mutationTokenRef.current === opToken) {
        setIsSaving(false);
      }
    }
  };

  const handleDeleteModifierRow = async (row: IClientModifierMatrixRow) => {
    const opToken = (mutationTokenRef.current += 1);
    setIsSaving(true);
    try {
      await deleteRow(row.id).unwrap();
      if (!opStillOwnsState(opToken, clientId)) return;
      await refetchRows();
    } catch {
      // The slice's rejected reducer already surfaces the server error toast; swallow the
      // rejection here so the finally block can reset the saving state.
    } finally {
      if (mountedRef.current && mutationTokenRef.current === opToken) {
        setIsSaving(false);
      }
    }
  };

  const handleCancelEditModifierRow = () => {
    // If adding a new row (identified by null id), remove it from the list.
    setModifierRows((current) => current.filter((row) => row.id !== null));
    setEditableModifierRowId(null);
    setIsNewModifierRowAdding(false);
  };

  const hasNothingToShow = modifierRows.length === 0 && !isNewModifierRowAdding;

  return (
    <FBoxContainer sx={{ borderRadius: '20px', mt: 3, boxShadow: 'none' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Box ml={1} mb={2} sx={{ display: 'flex', width: '100%', alignItems: 'center' }}>
          <Typography textAlign="left" variant="h4">
            Billing Modifiers
          </Typography>
        </Box>
        {loading && (
          <Box data-testid="modifierMatrixLoading" sx={CENTER_MESSAGE_SX}>
            <CircularProgress />
          </Box>
        )}
        {!loading && loadError && (
          <Box data-testid="modifierMatrixLoadError" sx={CENTER_MESSAGE_SX}>
            <Typography variant="h6" color={FORTA_ERROR}>
              Couldn&apos;t load billing modifiers — please refresh to try again.
            </Typography>
          </Box>
        )}
        {!loading && !loadError && (
          <>
            {hasNothingToShow ? (
              <Box sx={CENTER_MESSAGE_SX}>
                <Typography variant="h6">
                  No modifier matrix rows for this client.
                </Typography>
              </Box>
            ) : (
              <Box sx={MODIFIER_ROW_SCROLL_SX}>
                <ModifiersTableHeader />
                {modifierRows.map((row, index) => {
                  const isAddingRow = isNewModifierRowAdding && row.id === null;
                  const isEditingRow = row.id !== null && editableModifierRowId === row.id;
                  return (
                    <ClientModifierMatrixRow
                      key={row.id ?? `new-${index}`}
                      row={row}
                      serviceCodeOptions={serviceCodeOptions}
                      payorOptions={payorOptions}
                      isAdding={isAddingRow}
                      isEditing={isEditingRow}
                      isDisabled={
                        loading || isSaving || (isManagingModifierRow && !isAddingRow && !isEditingRow)
                      }
                      onSave={handleSaveModifierRow}
                      onCancel={handleCancelEditModifierRow}
                      onEdit={() => row.id !== null && handleEditModifierRow(row.id)}
                      onDelete={handleDeleteModifierRow}
                    />
                  );
                })}
              </Box>
            )}
            <Box>
              <FHButton
                data-testid="addModifierRow"
                sx={ADD_ROW_BUTTON_SX}
                startIcon={<AddIcon />}
                disabled={isManagingModifierRow || isSaving || loading}
                onClick={handleAddModifierRow}
              >
                Add row
              </FHButton>
            </Box>
          </>
        )}
      </Box>
    </FBoxContainer>
  );
};

export default ClientModifierMatrixSection;
